import { type VariantProps, cva } from 'class-variance-authority';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import type { ImageAspectRatio } from '@beat-venues/common-types';

export const PIXEL_DENSITY = {
  mobile: 3,
  desktop: 2,
};

export const SOURCE_BREAKPOINTS = {
  xs: 480 * PIXEL_DENSITY['mobile'],
  sm: 768 * PIXEL_DENSITY['desktop'],
  md: 1024 * PIXEL_DENSITY['desktop'],
  lg: 1280 * PIXEL_DENSITY['desktop'],
  xl: 1536 * PIXEL_DENSITY['desktop'],
};

export interface ContentfulImageProps
  extends VariantProps<typeof ContentfulImageStyles> {
  src: string;
  alt: string;
  caption?: string;
  width?: number;
  sizes?: string;
  isSvg?: boolean;
  className?: string;
  wrapperClassName?: string;
  testId?: string;
  aspectRatio?: ImageAspectRatio;
}

export const ContentfulImageStyles = cva('', {
  variants: {
    rounded: {
      none: null,
      md: 'rounded-md',
      xl: 'rounded-xl',
    },
    aspectRatio: {
      BANNER: 'aspect-banner object-cover',
      STORY: 'aspect-story object-cover',
      IMAGE: null,
    },
  },
  defaultVariants: {
    rounded: 'md',
  },
});

export const ContentfulImage = ({
  src,
  alt,
  caption,
  width,
  sizes,
  isSvg,
  rounded,
  className,
  wrapperClassName,
  testId = 'contentful-image',
  aspectRatio,
}: ContentfulImageProps): JSX.Element | null => {
  const getSrcSet = (fileType: string) => {
    if (width) {
      return `${src}?fm=${fileType}&w=${
        SOURCE_BREAKPOINTS['xs'] < width ? SOURCE_BREAKPOINTS['xs'] : width
      } 480w,
    ${src}?fm=${fileType}&w=${
        SOURCE_BREAKPOINTS['sm'] < width ? SOURCE_BREAKPOINTS['sm'] : width
      } 768w,
    ${src}?fm=${fileType}&w=${
        SOURCE_BREAKPOINTS['md'] < width ? SOURCE_BREAKPOINTS['md'] : width
      } 1024w,
    ${src}?fm=${fileType}&w=${
        SOURCE_BREAKPOINTS['lg'] < width ? SOURCE_BREAKPOINTS['lg'] : width
      } 1280w,
    ${src}?fm=${fileType}&w=${
        SOURCE_BREAKPOINTS['xl'] < width ? SOURCE_BREAKPOINTS['xl'] : width
      } 1536w`;
    }
    return `${src}?fm=${fileType}&w=${SOURCE_BREAKPOINTS['xs']} 480w,
    ${src}?fm=${fileType}&w=${SOURCE_BREAKPOINTS['sm']} 768w,
    ${src}?fm=${fileType}&w=${SOURCE_BREAKPOINTS['md']} 1024w,
    ${src}?fm=${fileType}&w=${SOURCE_BREAKPOINTS['lg']} 1280w,
    ${src}?fm=${fileType}&w=${SOURCE_BREAKPOINTS['xl']} 1536w`;
  };

  const getSizes = useMemo(() => {
    if (width) {
      return (
        `(max-width: 480px) ${
          SOURCE_BREAKPOINTS['xs'] < width ? SOURCE_BREAKPOINTS['xs'] : width
        }px, ` +
        `(max-width: 768px) ${
          SOURCE_BREAKPOINTS['sm'] < width ? SOURCE_BREAKPOINTS['sm'] : width
        }px, ` +
        `(max-width: 1024px) ${
          SOURCE_BREAKPOINTS['md'] < width ? SOURCE_BREAKPOINTS['md'] : width
        }px, ` +
        `(max-width: 1280px) ${
          SOURCE_BREAKPOINTS['lg'] < width ? SOURCE_BREAKPOINTS['lg'] : width
        }px, ` +
        `${
          SOURCE_BREAKPOINTS['xl'] < width ? SOURCE_BREAKPOINTS['xl'] : width
        }px`
      );
    }
    return (
      `(max-width: 480px) ${SOURCE_BREAKPOINTS['xs']}px, ` +
      `(max-width: 768px) ${SOURCE_BREAKPOINTS['sm']}px, ` +
      `(max-width: 1024px) ${SOURCE_BREAKPOINTS['md']}px, ` +
      `(max-width: 1280px) ${SOURCE_BREAKPOINTS['lg']}px, ` +
      `${SOURCE_BREAKPOINTS['xl']}px`
    );
  }, [width]);

  const generatedSizes = sizes ?? getSizes;

  return (
    <figure data-testid={testId} className={wrapperClassName}>
      <picture>
        {isSvg ? (
          <img
            alt={alt}
            className={twMerge(
              ContentfulImageStyles({
                rounded,
                className,
                aspectRatio,
              })
            )}
            src={src}
          />
        ) : (
          <>
            <source
              sizes={generatedSizes}
              srcSet={getSrcSet('webp')}
              type="image/webp"
            />
            <source
              sizes={generatedSizes}
              srcSet={getSrcSet('jpg')}
              type="image/jpeg"
            />
            <img
              alt={alt}
              className={twMerge(
                ContentfulImageStyles({
                  rounded,
                  className,
                  aspectRatio,
                })
              )}
              sizes={generatedSizes}
              src={src + '?fm=jpg'}
            />
          </>
        )}
      </picture>
      {caption && <figcaption className="pt-2 text-xs">{caption}</figcaption>}
    </figure>
  );
};
